import BigNumber from 'bignumber.js'
import { Token } from 'config/types'
import { WageringConditionBonus } from 'config/types/bonus/userBonus'
import React, { useCallback, useState } from 'react'
import { Trans } from 'react-i18next'
import { useTokenUsdPrice } from 'state/app/hooks'
import styled, { css } from 'styled-components'
import Box from 'UIKit/Box/Box'
import { BoxProps, FlexProps } from 'UIKit/Box/types'
import Text from 'UIKit/Text/Text'
import { getFullDisplayBalance } from 'utils/formatBalance'
import { useDisplayBonusName, useValidateBonus, ValidatedBonusStatusEnums } from 'views/BonusCenter/hooks'

import useModal, { useCloseById } from 'hooks/useModal'
import { RowMiddle } from 'layout/Components/Row'
import { Icons } from 'svgs'
import Flex from 'UIKit/Box/Flex'
import Button from 'UIKit/Button'
import IconButton from 'UIKit/Button/IconButton'
import { Checkbox } from 'UIKit/Checkbox'
import Image from 'UIKit/Image'
import { TabProps } from 'UIKit/Tab'
import TransText from 'UIKit/Text/TransText'
import BonusDetailsModal from 'views/BonusCenter/components/BonusDetailsModal'
import WaitingDepositBonusCountdown from './WaitingDepositBonusCountdown'

const BONUS_DETAIL_MODAL_ID = 'bonus-detail-modal'

const ActionButton = ({ handleClick }: { handleClick: () => void }) => {
  return (
    <Box p="0px 24px" width="100%">
      <Button width="100%" height="48px" onClick={handleClick} style={{ borderRadius: '4px' }}>
        <TransText fontSize="14px" fontWeight="600">
          Got It
        </TransText>
      </Button>
    </Box>
  )
}

export const WaitingDepositBonusItemContent: React.FC<
  {
    bonus: WageringConditionBonus
    selectedToken: Token
    disabled?: boolean
    isSelected?: boolean
    isShowCheckbox?: boolean
    isShowAccess?: boolean
    onExpired?: () => void
    onSelectBonus?: () => void
  } & FlexProps
> = ({
  bonus,
  selectedToken,
  onSelectBonus,
  onExpired,
  disabled,
  isSelected,
  isShowCheckbox,
  isShowAccess,
  ...props
}) => {
  const [name] = useDisplayBonusName(bonus.info?.name)
  const tokenPrice = useTokenUsdPrice(selectedToken)

  const [isExpired, setIsExpired] = useState(false)

  const [onPresentBonusDetails] = useModal(BonusDetailsModal, BONUS_DETAIL_MODAL_ID)
  const closeModal = useCloseById()

  const handleBonusExpired = useCallback(() => {
    setIsExpired(true)

    if (onExpired) onExpired()
  }, [onExpired])

  const isDisabled = bonus.condition?.isExpired || isExpired || disabled

  const handleOpenBonusDetails = () => {
    if (isDisabled) return
    onPresentBonusDetails({
      bonus,
      ActionButton: <ActionButton handleClick={() => closeModal(BONUS_DETAIL_MODAL_ID)} />,
    })
  }

  return (
    bonus?.info && (
      <StyleContainer
        onClick={isDisabled ? () => {} : handleOpenBonusDetails}
        $active={isSelected}
        $expire={bonus.condition?.isExpired}
        $disabled={isDisabled}
        {...props}
      >
        <RowMiddle gap="12px" flex={1}>
          <Image src={bonus.info.image} width={40} height={40} alt="bonus image" />
          <Box>
            <Text fontSize={['16px', '', '', '14px']} bold color={isDisabled && !isSelected ? 'textAlt1' : 'text'}>
              {name}
            </Text>

            <RowMiddle my="4px" gap="2px">
              <Text
                display="flex"
                fontSize="12px"
                fontWeight="300"
                color={isDisabled && !isSelected ? 'textAlt1' : 'textSubtle'}
              >
                <Trans>Min Deposit</Trans>:
                <Text ml="4px" fontSize="1em" color="currentColor" fontWeight={300}>
                  ${bonus.minDeposit}
                  {'  '}(~
                  {getFullDisplayBalance(
                    new BigNumber(bonus.minDeposit)
                      .dividedBy(tokenPrice)
                      .toFixed(tokenPrice.gt(10) ? 6 : 2, BigNumber.ROUND_CEIL),
                    0,
                    tokenPrice.gt(10) ? 6 : 2,
                  )}{' '}
                  {selectedToken.name})
                </Text>
              </Text>
            </RowMiddle>
            <WaitingDepositBonusCountdown
              color={isDisabled && !isSelected ? 'textAlt1' : 'textAlt699'}
              expiryDate={bonus.condition.expiryDate}
              onBonusExpired={handleBonusExpired}
            />
          </Box>
        </RowMiddle>

        {isShowCheckbox && (
          <Box position="relative" width="fit-content" height="fit-content">
            <StyledCheck $disabled={isDisabled && !isSelected} value={isSelected} onValueChanged={() => {}} />

            <Box
              position="absolute"
              top="-16px"
              left="-16px"
              right="-16px"
              bottom="-16px"
              borderRadius="50%"
              style={{ cursor: 'pointer' }}
              onClick={
                isDisabled
                  ? () => {}
                  : (e: React.MouseEvent) => {
                      e.stopPropagation()
                      onSelectBonus()
                    }
              }
              zIndex={1}
            />
          </Box>
        )}
        {isShowAccess && (
          <IconButton width={12}>
            <Icons.ChevronRightIcon width="8px" />
          </IconButton>
        )}
      </StyleContainer>
    )
  )
}

const WaitingDepositBonusItem: React.FC<
  BoxProps & {
    bonus: WageringConditionBonus
    selectedToken: Token
    disabled?: boolean
    isSelected?: boolean
    onSelectBonus: () => void
    onExpired?: () => void
  } & TabProps
> = ({ bonus, selectedToken, isSelected, disabled, onSelectBonus, onExpired, ...props }) => {
  const validatedResult: ValidatedBonusStatusEnums = useValidateBonus(bonus)

  return (
    ValidatedBonusStatusEnums.Available === validatedResult &&
    bonus &&
    bonus?.id &&
    selectedToken && (
      <WaitingDepositBonusItemContent
        selectedToken={selectedToken}
        isSelected={isSelected}
        bonus={bonus}
        disabled={disabled}
        onSelectBonus={onSelectBonus}
        isShowCheckbox
        onExpired={onExpired}
        {...props}
      />
    )
  )
}

const StyleContainer = styled(Flex)<{ $active: boolean; $expire?: boolean; $disabled: boolean }>`
  border: 1px solid transparent;

  flex-direction: row;
  align-items: center;
  width: 100%;
  height: fit-content;

  backdrop-filter: blur(10px);
  gap: 12px;
  min-height: 62px;
  padding: 6px;
  border-radius: 12px;

  ${({ $active, theme }) =>
    $active
      ? css`
          background: #06c2701e;
          border-color: ${theme.colors.backgroundAlt13};
          backdrop-filter: blur(10px);
        `
      : css``};

  ${({ $expire, theme }) =>
    $expire
      ? css`
          background: #ed6a5e1e;
          border-color: ${theme.colors.error};
          backdrop-filter: blur(10px);
        `
      : css``};

  ${({ $disabled }) =>
    $disabled
      ? css`
          background: linear-gradient(132deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0) 100%),
            radial-gradient(at 39% 66%, #2af6ff20 -215%, transparent 70%);
        `
      : css`
          cursor: pointer;
        `};
`

const StyledCheck = styled(Checkbox)<{ $disabled: boolean }>`
  width: 20px;
  height: 20px;

  border-width: 2px;
  border-style: solid;
  border-color: ${({ theme }) => theme.colors.strokeAlt4};
  background: transparent;
  border-radius: 50%;

  margin-left: auto;

  &:after {
    display: none;
  }
  &:checked:after {
    display: flex;
  }
  &:checked {
    border-color: ${({ theme }) => theme.colors.backgroundAlt13};
    background: ${({ theme }) => theme.colors.backgroundAlt13};
  }

  ${({ $disabled }) =>
    $disabled
      ? css`
          border-color: #32363d !important;
        `
      : css`
          border-color: ${({ theme }) => theme.colors.textAlt699};
        `}
`

export default WaitingDepositBonusItem
